'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    services: [
      { label: 'IT Infrastructure', href: '/services-overview' },
      { label: 'Cybersecurity', href: '/services-overview' },
      { label: 'Cloud Solutions', href: '/services-overview' },
      { label: 'Managed Services', href: '/services-overview' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Industries', href: '/industry-solutions' },
      { label: 'Contact', href: '/contact' },
      { label: 'Client Portal', href: '/client-portal' }
    ],
    resources: [
      { label: 'Blog', href: '#' },
      { label: 'Case Studies', href: '#' },
      { label: 'Whitepapers', href: '#' },
      { label: 'Webinars', href: '#' }
    ]
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'AtSymbolIcon', href: '#' },
    { name: 'Facebook', icon: 'UserGroupIcon', href: '#' },
    { name: 'YouTube', icon: 'PlayCircleIcon', href: '#' }
  ];

  return (
    <footer className="bg-primary text-white">
      <div className="container mx-auto px-4 py-12">
        {/* Top Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-accent to-brand-secondary rounded-lg flex items-center justify-center">
                <span className="text-white font-headline font-bold text-xl">TS</span>
              </div>
              <div className="flex flex-col">
                <span className="font-headline font-bold text-lg leading-tight">TechSolutions</span>
                <span className="font-headline font-semibold text-xs text-accent leading-tight">Pro</span>
              </div>
            </Link>
            <p className="text-sm text-white/80 font-body mb-4">
              Your strategic technology partner for business growth. Enterprise-level expertise meets personalized service.
            </p>
            <div className="flex items-center space-x-2 bg-error/20 px-4 py-2 rounded-lg border border-error/30">
              <div className="w-2 h-2 bg-error rounded-full animate-pulse-slow" />
              <span className="text-sm font-cta font-semibold">24/7 Emergency Support</span>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="font-headline font-bold text-lg mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link, index) => (
                <li key={index}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/80 hover:text-accent font-body transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Middle Section - Contact Info */}
        <div className="grid md:grid-cols-3 gap-6 py-8 border-t border-white/10">
          <div className="flex items-start space-x-3">
            <Icon name="PhoneIcon" size={20} variant="outline" className="text-accent mt-1" />
            <div>
              <p className="text-sm font-cta font-semibold mb-1">Call Us</p>
              <p className="text-sm text-white/80 font-body">+966 55 2258277</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-accent mt-1" />
            <div>
              <p className="text-sm font-cta font-semibold mb-1">Email Us</p>
              <p className="text-sm text-white/80 font-body">info@centronics.com.sa</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="MapPinIcon" size={20} variant="outline" className="text-accent mt-1" />
            <div>
              <p className="text-sm font-cta font-semibold mb-1">Visit Us</p>
              <p className="text-sm text-white/80 font-body">Office 14 baldiyah street, Aziziya, Jeddah, Saudi Arabia</p>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/10">
          <p className="text-sm text-white/60 font-body mb-4 md:mb-0">
            {isHydrated ? `© ${currentYear}` : '© 2026'} Centronics IT Soloutions. All rights reserved.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center space-x-4">
            {socialLinks.map((social, index) => (
              <a
                key={index}
                href={social.href}
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-accent rounded-full transition-colors duration-200"
                aria-label={social.name}
              >
                <Icon name={social.icon as any} size={20} variant="outline" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;